import {useMutation, type UseMutationResult} from '@github-ui/react-query'
import {transferIssueMutation as transferIssueMutationQueryId} from '@github-ui/server-persisted-queries'
import type {
  TransferIssueInput as ServerTransferIssueInput,
  TransferIssueMutationMutation,
} from '@github-ui/server-persisted-queries/types/transferIssueMutation'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  TransferIssueInput,
  transferIssueMutation,
  transferIssueMutation$data,
} from './__generated__/transferIssueMutation.graphql'
import {issueLifecyclePath, submitIssueLifecycleMutation} from './issue-lifecycle-mutation'

export type TransferIssueRouteInput = Omit<ServerTransferIssueInput, 'issueId' | 'clientMutationId'>
// RELAY-CLEANUP(transferIssueMutation): Drop the Relay response type when the fallback is removed.
export type TransferIssueMutationResponse = transferIssueMutation$data | TransferIssueMutationMutation

type IssueMutationProps = {
  environment: Environment
  input: TransferIssueInput
  onError?: (error: Error) => void
  onCompleted?: (response: transferIssueMutation$data) => void
}

export function commitTransferIssueMutation({
  environment,
  input: {issueId, repositoryId, createLabelsIfMissing},
  onError,
  onCompleted,
}: IssueMutationProps) {
  const inputHash: TransferIssueInput = {
    issueId,
    repositoryId,
    createLabelsIfMissing,
  }

  return commitMutation<transferIssueMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation transferIssueMutation($input: TransferIssueInput!) @raw_response_type {
        transferIssue(input: $input) {
          issue {
            url
          }
          errors {
            message
          }
        }
      }
    `,
    variables: {
      input: inputHash,
    },
    onError: error => onError && onError(error),
    onCompleted: response => onCompleted && onCompleted(response),
  })
}

export function useTransferIssueMutation({
  owner,
  repository,
  number,
  onCompleted,
  onError,
}: {
  owner: string
  repository: string
  number: number
  onCompleted?: (response: TransferIssueMutationMutation) => void
  onError?: (error: Error) => void
}): UseMutationResult<TransferIssueMutationMutation, Error, {input: TransferIssueRouteInput}> {
  return useMutation({
    mutationFn: ({input}) => submitTransferIssueMutation({owner, repository, number, input}),
    onSuccess: onCompleted,
    onError,
  })
}

export function submitTransferIssueMutation({
  owner,
  repository,
  number,
  input,
}: {
  owner: string
  repository: string
  number: number
  input: TransferIssueRouteInput
}) {
  return submitIssueLifecycleMutation<TransferIssueMutationMutation>({
    path: `${issueLifecyclePath(owner, repository, number)}/transfer.json`,
    method: 'POST',
    queryId: transferIssueMutationQueryId,
    input,
    errorMessage: 'Issue transfer mutation failed',
    isSuccess: data => data.transferIssue?.issue != null,
    payloadErrors: data => data.transferIssue?.errors,
  })
}
